import React, { useState, useRef, useEffect } from 'react';
import { Upload, Send, Loader2, CheckCircle2 } from 'lucide-react';
import { Button } from './ui/button';
import '../styles/ChatbotDemo.css';

const ChatbotDemo = () => {
  const [messages, setMessages] = useState([
  {
    role: 'assistant',
    content: 'Hello! Upload your building drawing and tell me what you want to see.',
    timestamp: new Date()
  }]
  );
  const [inputValue, setInputValue] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setUploadedFile(file);
      setMessages((prev) => [
      ...prev,
      {
        role: 'user',
        content: `Uploaded: ${file.name}`,
        timestamp: new Date(),
        isFile: true
      }]
      );

      // Mock AI response
      setTimeout(() => {
        setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Great! I can see your floor plan. What would you like me to generate? For example: "Create a photorealistic 3D render with modern materials"',
          timestamp: new Date()
        }]
        );
      }, 1000);
    }
  };

  const handleSendMessage = () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      role: 'user',
      content: inputValue,
      timestamp: new Date()
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setIsProcessing(true);

    // Mock AI processing
    setTimeout(() => {
      setIsProcessing(false);
      setMessages((prev) => [
      ...prev,
      {
        role: 'assistant',
        content: 'Perfect! I\'m generating your 3D visualization now. This will take approximately 30-45 seconds...',
        timestamp: new Date()
      }]
      );

      // Mock completion
      setTimeout(() => {
        setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: '✨ Your 3D render is ready! I\'ve created a photorealistic visualization with modern materials, natural lighting, and detailed textures.',
          timestamp: new Date(),
          isComplete: true,
          preview: '/floorplan_3d.png'
        }]
        );
      }, 2000);
    }, 1500);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className="chatbot-demo-container">
      <div className="chatbot-window">
        <div className="chatbot-header">
          <div className="chatbot-status">
            <div className="status-indicator"></div>
            <span className="body-medium">AI Assistant</span>
          </div>
          <span className="body-small chatbot-subtitle">Powered by Ezai Build</span>
        </div>

        <div className="chatbot-messages">
          {messages.map((message, index) =>
          <div key={index} className={`message message-${message.role}`}>
              <div className="message-content">
                {message.isFile ?
              <div className="file-upload-message">
                    <Upload size={20} />
                    <span className="body-medium">{message.content}</span>
                    <CheckCircle2 size={18} className="file-check" />
                  </div> :

              <p className="body-medium">{message.content}</p>
              }
                {message.preview &&
              <div className="message-preview">
                    <img src={message.preview} alt="Generated 3D render" />
                  </div>
              }
              </div>
              <span className="message-time body-small">
                {message.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
              </span>
            </div>
          )}
          {isProcessing &&
          <div className="message message-assistant">
              <div className="message-content processing">
                <Loader2 size={20} className="spinner" />
                <span className="body-medium">Processing...</span>
              </div>
            </div>
          }
          <div ref={messagesEndRef} />
        </div>

        <div className="chatbot-input-container">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".dwg,.rvt,.png,.jpg,.jpeg"
            style={{ display: 'none' }} />

          <Button
            className="btn-secondary upload-btn"
            onClick={() => fileInputRef.current?.click()}>

            <Upload size={20} />
          </Button>
          <input
            type="text"
            className="chatbot-input"
            placeholder="Describe what you want to see..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress} />

          <Button
            className="btn-primary send-btn"
            onClick={handleSendMessage}
            disabled={!inputValue.trim()}>

            <Send size={20} />
          </Button>
        </div>
      </div>

      <div className="demo-instructions">
        <h3 className="heading-2">Try it yourself</h3>
        <ul className="instructions-list">
          <li className="body-medium">
            <span className="instruction-number">1</span>
            Upload your 2D floor plan (DWG, RVT, or image)
          </li>
          <li className="body-medium">
            <span className="instruction-number">2</span>
            Describe the visualization you want
          </li>
          <li className="body-medium">
            <span className="instruction-number">3</span>
            Get your photorealistic 3D render in seconds
          </li>
        </ul>
      </div>
    </div>);

};

export default ChatbotDemo;